import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { BookOpenCheck, Plus, Layers, GraduationCap, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";
import { extractDominantColor } from "../lib/dominantColor";

const EMPTY_FORM = { title: "", description: "", cover_color: "#0D9488", cover_image_url: "", published: true, admin_only: false, access_tier: "free", price_usd: "" };

export default function Playbook() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [tierFilter, setTierFilter] = useState("all"); // "all" | "free" | "premium"

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        api.get("/playbooks"),
        api.get("/playbooks/progress/summary").catch(() => ({ data: {} })),
      ]);
      setItems(listRes.data);
      setProgress(summaryRes.data || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const publishedCount = useMemo(() => items.filter((p) => p.published).length, [items]);
  const filteredItems = useMemo(() => {
    if (tierFilter === "all") return items;
    if (tierFilter === "premium") return items.filter((p) => p.access_tier === "premium");
    return items.filter((p) => (p.access_tier || "free") === "free");
  }, [items, tierFilter]);
  const premiumCount = useMemo(() => items.filter((p) => p.access_tier === "premium").length, [items]);

  const openCreate = () => { setForm(EMPTY_FORM); setDialogOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const priceRaw = String(form.price_usd || "").trim();
      const priceNum = priceRaw === "" ? null : Number(priceRaw);
      await api.post("/playbooks", {
        ...form,
        title: form.title.trim(),
        cover_image_url: form.cover_image_url?.trim() || null,
        access_tier: form.access_tier || "free",
        price_usd: form.access_tier === "premium" && Number.isFinite(priceNum) && priceNum > 0 ? priceNum : null,
      });
      toast.success("Playbook created");
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6" data-testid="playbook-page">
        <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// The Amino Exchange</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-slate-900">
              Playbook
            </h1>
            <p className="text-slate-600 mt-2 max-w-xl text-sm">
              Curated guides, videos, links, and supply lists from the team. New here? Start with any card below.
            </p>
          </div>
          {isStaff && (
            <Button
              onClick={openCreate}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none uppercase tracking-widest font-semibold"
              data-testid="playbook-new"
            >
              <Plus className="w-3.5 h-3.5 mr-2" /> New Playbook
            </Button>
          )}
        </header>

        {loading ? (
          <div className="text-slate-500 font-mono-num text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-slate-200 p-10 text-center" data-testid="playbook-empty">
            <BookOpenCheck className="w-6 h-6 text-[#0D9488] mx-auto mb-2" />
            <div className="font-display font-black text-lg uppercase tracking-tight text-slate-900">No playbooks yet</div>
            <p className="text-slate-500 text-sm mt-1">
              {isStaff ? 'Click "New Playbook" to build your first one.' : "The team hasn't published any guides yet — check back soon."}
            </p>
          </div>
        ) : (
          <>
            {premiumCount > 0 && (
              <div className="flex items-center gap-2 mb-4" data-testid="playbook-tier-filter">
                {[
                  { key: "all", label: `All (${items.length})` },
                  { key: "free", label: `Free (${items.length - premiumCount})` },
                  { key: "premium", label: `★ Members Only (${premiumCount})` },
                ].map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setTierFilter(chip.key)}
                    className={`px-3 py-1.5 text-[10px] font-mono-num uppercase tracking-widest border transition-colors ${
                      tierFilter === chip.key
                        ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
                        : "border-slate-200 bg-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900"
                    }`}
                    data-testid={`playbook-tier-chip-${chip.key}`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="playbook-grid">
            {filteredItems.map((p) => {
              const done = progress[p.id] || 0;
              const total = p.lesson_count || 0;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
              <Link
                to={`/playbook/${p.id}`}
                key={p.id}
                className="group border border-slate-200 bg-white hover:border-[#0D9488] transition-colors flex flex-col relative overflow-hidden"
                data-testid={`playbook-card-${p.id}`}
              >
                {p.cover_image_url && (
                  <div
                    className="h-32 border-b border-slate-200 overflow-hidden flex items-center justify-center"
                    style={{ backgroundColor: p.cover_color || "#f8fafc" }}
                    data-testid={`playbook-card-cover-${p.id}`}
                  >
                    <img src={p.cover_image_url} alt="" className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: p.cover_color || "#0D9488" }}
                />
                {!p.published && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-[#0D9488] border border-[#0D9488]/40 px-2 py-0.5 bg-white">
                    <EyeOff className="w-2.5 h-2.5" /> Draft
                  </div>
                )}
                {p.admin_only && (
                  <div
                    className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5"
                    style={{ top: !p.published ? "36px" : "12px" }}
                    data-testid={`playbook-admin-only-${p.id}`}
                  >
                    <Lock className="w-2.5 h-2.5" /> Admin only
                  </div>
                )}
                {p.access_tier === "premium" && (
                  <div
                    className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-white bg-[#0D9488] px-2 py-0.5"
                    data-testid={`playbook-members-only-${p.id}`}
                  >
                    {p.is_unlocked ? "✓ Unlocked" : (p.price_usd ? `★ $${Number(p.price_usd).toFixed(2)}` : "★ Members Only")}
                  </div>
                )}
                <div className="font-display font-black text-xl uppercase tracking-tight text-slate-900 group-hover:text-[#0D9488] transition-colors">
                  {p.title}
                </div>
                {p.description && (
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed line-clamp-3">{p.description}</p>
                )}
                <div className="mt-auto pt-4 space-y-2">
                  <div className="flex items-center gap-4 font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <Layers className="w-3 h-3 text-[#0D9488]" /> {p.section_count} sec
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <GraduationCap className="w-3 h-3 text-[#0D9488]" /> {p.lesson_count} lesson{p.lesson_count === 1 ? "" : "s"}
                    </span>
                  </div>
                  {total > 0 && done > 0 && (
                    <div data-testid={`playbook-card-progress-${p.id}`}>
                      <div className="flex items-center justify-between text-[9px] font-mono-num uppercase tracking-widest text-slate-500 mb-1">
                        <span>Your progress</span>
                        <span>{done} / {total} · {pct}%</span>
                      </div>
                      <div className="h-1 bg-slate-50">
                        <div className="h-full bg-[#0D9488]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )}
                </div>
                </div>
              </Link>
              );
            })}
          </div>
          </>
        )}

        {isStaff && (
          <div className="mt-6 font-mono-num text-[10px] uppercase tracking-widest text-slate-400">
            {publishedCount} published · {items.length - publishedCount} draft
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-lg" data-testid="playbook-new-dialog">
          <DialogHeader>
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// New</div>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl font-black">Playbook</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs mt-1">A new playbook groups sections and lessons for teaching a workflow.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                required maxLength={140} className="bg-slate-50 border-slate-200 rounded-none h-11"
                data-testid="playbook-form-title" placeholder="Reconstitution 101" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Description</Label>
              <Textarea rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-slate-50 border-slate-200 rounded-none resize-none"
                data-testid="playbook-form-description"
                placeholder="Everything a first-timer needs to safely reconstitute a peptide vial." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Cover Image URL (optional)</Label>
              <Input value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                onBlur={async (e) => {
                  const url = e.target.value.trim();
                  if (!url) return;
                  const dominant = await extractDominantColor(url);
                  if (dominant) setForm((prev) => ({ ...prev, cover_color: dominant }));
                }}
                placeholder="https://…"
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num text-sm"
                data-testid="playbook-form-cover-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Cover Color</Label>
                <Input type="color" value={form.cover_color || "#0D9488"}
                  onChange={(e) => setForm({ ...form, cover_color: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 p-1"
                  data-testid="playbook-form-color" />
              </div>
              <label className="flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 cursor-pointer">
                <input type="checkbox" checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="accent-[#0D9488]"
                  data-testid="playbook-form-published" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-slate-800 font-mono-num">Published</span>
              </label>
            </div>
            <label className={`flex items-start gap-2 border rounded-none px-3 py-2.5 cursor-pointer transition-colors ${form.admin_only ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}>
              <input type="checkbox" checked={form.admin_only}
                onChange={(e) => setForm({ ...form, admin_only: e.target.checked })}
                className="accent-amber-500 mt-0.5"
                data-testid="playbook-form-admin-only" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-800 font-mono-num flex items-center gap-1.5">
                  Admin Only
                  {form.admin_only && (
                    <span className="text-[9px] font-black text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 tracking-widest">HIDDEN</span>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 leading-relaxed mt-0.5 normal-case tracking-normal">
                  Only admins see and open this playbook. Moderators and regular users won't see it in the library.
                </p>
              </div>
            </label>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Access Tier</Label>
              <select value={form.access_tier || "free"}
                onChange={(e) => setForm({ ...form, access_tier: e.target.value })}
                className="w-full h-11 bg-slate-50 border border-slate-200 rounded-none px-3 text-sm text-slate-900 focus:border-[#0D9488] focus:outline-none"
                data-testid="playbook-form-access-tier">
                <option value="free">Free — anyone signed in can read</option>
                <option value="premium">Members Only — locked for non-premium users</option>
              </select>
            </div>
            {form.access_tier === "premium" && (
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Per-playbook Price (USD)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 font-mono-num text-sm">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price_usd}
                    onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
                    placeholder="9.99 — leave blank if Members-Only-only"
                    className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num tabular-nums"
                    data-testid="playbook-form-price"
                  />
                </div>
                <p className="text-[10px] text-slate-500 font-mono-num uppercase tracking-widest leading-relaxed">
                  Blank → only global-Premium members read it. Set → users can buy just this one via PayPal.
                </p>
              </div>
            )}
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}
                className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800"
                data-testid="playbook-form-cancel">Cancel</Button>
              <Button type="submit" disabled={saving}
                className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none uppercase tracking-widest font-semibold"
                data-testid="playbook-form-submit">
                {saving ? "Creating…" : "Create Playbook"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
